import { arithmeticGeometricMean, DEG, TAU } from '@physlab/core';
import { createQuestion, fmt, selectTemplates } from '../questionUtils.js';

export function buildPendulumQuestions({ params, rng, difficultyLevel, exerciseCount, thinkingCount }) {
  const L = params.length;
  const g = params.gravity;
  const m = params.mass;
  const theta0 = params.initialAngle;
  const theta = theta0 * DEG;
  const period = TAU * Math.sqrt(L / g);
  const exactPeriod = period / arithmeticGeometricMean(1, Math.cos(theta / 2));
  const vmax = Math.sqrt(2 * g * L * (1 - Math.cos(theta)));
  const tension = m * g * (3 - 2 * Math.cos(theta));
  const energy = m * g * L * (1 - Math.cos(theta));

  const exercises = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `pendulum-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '单摆周期公式',
          prompt: `一单摆的摆长 $L=${fmt(L, 2)}\\ \\mathrm{m}$，当地重力加速度 $g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。最大摆角较小，可视为简谐振动。求它的振动周期 $T$（保留两位小数）。`,
          formula: 'T=2\\pi\\sqrt{L/g}',
          answer: `$T\\approx ${fmt(period, 2)}\\ \\mathrm{s}$`,
          answerValue: period,
          unit: 's',
          solution: [
            `代入单摆周期公式：$T=2\\pi\\sqrt{L/g}=2\\pi\\sqrt{${fmt(L, 2)}/${fmt(g, 1)}}$。`,
            `先算根号：$\\sqrt{${fmt(L / g, 4)}}\\approx ${fmt(Math.sqrt(L / g), 4)}$。`,
            `$T\\approx 2\\times3.1416\\times${fmt(Math.sqrt(L / g), 4)}\\approx ${fmt(period, 3)}\\ \\mathrm{s}$，取两位小数为 $${fmt(period, 2)}\\ \\mathrm{s}$。`,
          ],
          knowledge: ['单摆周期公式', '小角度近似'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `pendulum-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '单摆能量转化',
          prompt: `摆球质量 $m=${fmt(m, 2)}\\ \\mathrm{kg}$，摆长 $L=${fmt(L, 2)}\\ \\mathrm{m}$，最大摆角 $\\theta_0=${fmt(theta0, 0)}^\\circ$，当地 $g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。忽略空气阻力，求摆球经过最低点时的速度大小 $v$。`,
          formula: 'mgL(1-\\cos\\theta_0)=\\tfrac12mv^2',
          answer: `$v\\approx ${fmt(vmax, 2)}\\ \\mathrm{m/s}$`,
          answerValue: vmax,
          unit: 'm/s',
          solution: [
            `由静止释放到最低点，只有重力做功，机械能守恒：$mgL(1-\\cos\\theta_0)=\\tfrac12mv^2$。`,
            `质量 $m$ 约去，$v=\\sqrt{2gL(1-\\cos\\theta_0)}$。`,
            `代入数据：$v=\\sqrt{2\\times${fmt(g, 1)}\\times${fmt(L, 2)}\\times(1-\\cos ${fmt(theta0, 0)}^\\circ)}\\approx ${fmt(vmax, 3)}\\ \\mathrm{m/s}$。`,
          ],
          knowledge: ['机械能守恒', '单摆能量转化'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `pendulum-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '最低点绳张力',
          prompt: `摆球质量 $m=${fmt(m, 2)}\\ \\mathrm{kg}$，摆长 $L=${fmt(L, 2)}\\ \\mathrm{m}$，最大摆角 $\\theta_0=${fmt(theta0, 0)}^\\circ$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。忽略阻力，求摆球通过最低点时细线的拉力 $F_T$。`,
          formula: 'F_T=mg(3-2\\cos\\theta_0)',
          answer: `$F_T\\approx ${fmt(tension, 2)}\\ \\mathrm{N}$`,
          answerValue: tension,
          unit: 'N',
          solution: [
            `在最低点，绳张力与重力的合力提供向心力：$F_T-mg=\\dfrac{mv^2}{L}$。`,
            `由机械能守恒 $v^2=2gL(1-\\cos\\theta_0)$，代入得 $F_T=mg+2mg(1-\\cos\\theta_0)=mg(3-2\\cos\\theta_0)$。`,
            `代入数据：$F_T=${fmt(m, 2)}\\times${fmt(g, 1)}\\times(3-2\\cos ${fmt(theta0, 0)}^\\circ)\\approx ${fmt(tension, 2)}\\ \\mathrm{N}$。`,
          ],
          knowledge: ['向心力', '牛顿第二定律', '机械能守恒'],
        }),
    },
    {
      level: 2,
      build: (index) => {
        const n = 30;
        const measuredPeriod = Number(period.toFixed(2));
        const totalTime = Number((n * measuredPeriod).toFixed(1));
        const recoveredL = (g * totalTime * totalTime) / (4 * Math.PI * Math.PI * n * n);
        return createQuestion({
          id: `pendulum-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '用单摆测重力加速度',
          prompt: `某同学让单摆完成 $n=${n}$ 次全振动，测得总时间 $t=${fmt(totalTime, 1)}\\ \\mathrm{s}$。已知 $g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$，求该单摆的摆长 $L$（保留两位小数）。`,
          formula: 'T=\\dfrac{t}{n},\\quad L=\\dfrac{gT^2}{4\\pi^2}',
          answer: `$L\\approx ${recoveredL.toFixed(2)}\\ \\mathrm{m}$`,
          answerValue: recoveredL,
          unit: 'm',
          solution: [
            `先求周期：$T=\\dfrac{t}{n}=\\dfrac{${fmt(totalTime, 1)}}{${n}}\\approx ${fmt(totalTime / n, 4)}\\ \\mathrm{s}$。`,
            `由 $T=2\\pi\\sqrt{L/g}$ 得 $L=\\dfrac{gT^2}{4\\pi^2}$。`,
            `代入：$L=\\dfrac{${fmt(g, 1)}\\times${fmt(totalTime / n, 4)}^2}{4\\pi^2}\\approx ${fmt(recoveredL, 3)}\\ \\mathrm{m}$，即约 $${fmt(recoveredL, 2)}\\ \\mathrm{m}$。`,
          ],
          knowledge: ['单摆周期公式', '多次测量取平均'],
        });
      },
    },
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `pendulum-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '周期与摆长的比例关系',
          prompt: `在重力加速度不变的情况下，将单摆的摆长变为原来的 $4$ 倍，其他条件不变。它的周期变为原来的多少倍？`,
          formula: 'T\\propto\\sqrt{L}',
          answer: '$2$ 倍',
          answerValue: 2,
          unit: '倍',
          solution: [
            '由 $T=2\\pi\\sqrt{L/g}$ 可知，$g$ 不变时 $T\\propto\\sqrt{L}$。',
            '当 $L\'=4L$ 时，$T\'=2\\pi\\sqrt{4L/g}=2T$。',
            '所以周期变为原来的 $2$ 倍。',
          ],
          knowledge: ['单摆周期与摆长的关系'],
        }),
    },
    {
      level: 3,
      build: (index) => {
        const correction = (theta * theta) / 16;
        return createQuestion({
          id: `pendulum-ex-${index}`,
          type: 'exercise',
          level: 3,
          topic: '大角度周期修正',
          prompt: `最大摆角为 $\\theta_0=${fmt(theta0, 0)}^\\circ$ 时，小角度周期公式会低估实际周期。用近似修正 $T\\approx T_0\\left(1+\\dfrac{\\theta_0^2}{16}\\right)$（$\\theta_0$ 用弧度）估算周期相对增大的百分比（保留两位小数）。`,
          formula: '\\dfrac{T-T_0}{T_0}\\approx\\dfrac{\\theta_0^2}{16}',
          answer: `约增大 $${fmt(correction * 100, 2)}\\%$`,
          answerValue: correction * 100,
          unit: '%',
          solution: [
            `先把角度化为弧度：$\\theta_0=${fmt(theta0, 0)}^\\circ\\times\\dfrac{\\pi}{180}\\approx ${fmt(theta, 4)}\\ \\mathrm{rad}$。`,
            `代入修正项：$\\dfrac{\\theta_0^2}{16}\\approx\\dfrac{${fmt(theta, 4)}^2}{16}\\approx ${fmt(correction, 5)}$。`,
            `换算为百分比，周期约增大 $${fmt(correction * 100, 2)}\\%$。精确 AGM 结果为 $${fmt((exactPeriod / period - 1) * 100, 2)}\\%$，两者在中等摆角下吻合较好。`,
          ],
          knowledge: ['大角度单摆', '简谐近似的适用范围'],
        });
      },
    },
  ];

  const thinking = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `pendulum-th-${index}`,
          type: 'thinking',
          level: 1,
          topic: '周期与质量无关',
          prompt: '单摆的周期公式中不含摆球质量。这是否意味着换成更重的摆球，周期完全不变？请从动力学方程角度解释。',
          formula: '\\ddot\\theta+\\dfrac{g}{L}\\sin\\theta=0',
          answer:
            '在理想单摆中，周期与摆球质量无关。重力与惯性质量相等，质量在方程两边约去；即使是大幅摆动，周期也不含质量。',
          solution: [
            '沿圆弧切向列牛顿第二定律：$mg\\sin\\theta=-mL\\ddot\\theta$，即 $\\ddot\\theta+\\dfrac{g}{L}\\sin\\theta=0$。',
            '方程两边都含质量 $m$，可以约去，因此运动方程与质量无关。',
            '需要区分：改变质量会改变绳张力和机械能，但不改变周期。现实中空气阻力与质量有关，会间接影响振幅衰减。',
          ],
          knowledge: ['牛顿第二定律', '单摆周期', '惯性质量与引力质量'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `pendulum-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '大角度周期',
          prompt: '为什么摆角增大时，单摆的实际周期会比小角度公式 $T_0=2\\pi\\sqrt{L/g}$ 的结果更长？',
          formula: '\\sin\\theta<\\theta\\quad(\\theta>0)',
          answer:
            '大角度时回复力 $mg\\sin\\theta$ 比线性近似 $mg\\theta$ 小，摆球在两端附近停留更久，因此周期变长。',
          solution: [
            '小角度近似把 $\\sin\\theta$ 替换为 $\\theta$，高估了回复力。',
            '实际回复力偏小，摆球靠近最大摆角处运动更慢、停留时间更长。',
            '所以大角度周期总大于小角度公式值，且摆角越大，偏差越明显。',
          ],
          knowledge: ['简谐近似', '回复力', '单摆周期'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `pendulum-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '阻尼振动中的能量',
          prompt: '真实单摆会逐渐停下来。它的机械能去了哪里？为什么振幅衰减而周期变化通常不大？',
          formula: 'E=\\tfrac12mL^2\\omega^2+mgL(1-\\cos\\theta)',
          answer:
            '机械能通过空气阻力和悬点摩擦转化为内能（及声能）。轻阻尼时每个周期损失的能量占比很小，周期近似不变，但振幅按指数规律衰减。',
          solution: [
            '阻力做负功，机械能减少并转化为内能，总能量仍然守恒。',
            '轻阻尼条件下，阻尼力远小于回复力，振动“频率”只发生很小变化。',
            '振幅逐渐减小，最终停在平衡位置；能量并没有消失，而是变成了内能。',
          ],
          knowledge: ['阻尼振动', '能量转化与守恒'],
        }),
    },
    {
      level: 3,
      build: (index) =>
        createQuestion({
          id: `pendulum-th-${index}`,
          type: 'thinking',
          level: 3,
          topic: '实验方案设计',
          prompt: '用单摆测量当地重力加速度 $g$ 时，为什么要测量多次全振动的总时间，而不是只测一次？请再写出一种减小系统误差的措施。',
          formula: 'g=\\dfrac{4\\pi^2L}{T^2}',
          answer:
            '测多次全振动总时间再除以次数，可把人的反应时间误差平均分摊到每个周期，显著减小相对误差。还可用光电门自动计时、用图像法处理 T²–L 数据。',
          solution: [
            '秒表计时的主要误差来自开始和停止时的反应时间，约为固定值。测量 30 次全振动后取平均，单周期误差降为约 1/30。',
            '摆长应测量悬点到摆球球心的距离，而不是只测摆线长度。',
            '改变摆长获得多组 $(L,T^2)$ 数据，作 $T^2$–$L$ 图像，由斜率 $4\\pi^2/g$ 求 $g$，可减小偶然误差。',
          ],
          knowledge: ['实验误差分析', '图像法', '单摆测重力加速度'],
        }),
    },
  ];

  const selectedExercises = selectTemplates(rng, exercises, exerciseCount, difficultyLevel);
  const selectedThinking = selectTemplates(rng, thinking, thinkingCount, difficultyLevel);
  return {
    exercises: selectedExercises.map((template, index) => template.build(index + 1)),
    thinking: selectedThinking.map((template, index) => template.build(index + 1)),
  };
}
